import "./methodology.scss";

import PageTitleBreadcrumb from "@frontend/js/Components/Shared/PageTitleBreadcrumb/PageTitleBreadcrumb";
import AnimationIcon from "@frontend/js/Components/Shared/AnimationIcon/AnimationIcon";
import { useSectionNavigation } from "@frontend/js/hooks/useSectionNavigation";
import MetaHead from "@frontend/js/Components/Shared/MetaHead/MetaHead";
import ArrowNextIcon from "@frontend/images/icons/ArrowNextIcon";
import Form from "@frontend/js/Components/Shared/Form/Form";
import SiteLayout from "@frontend/js/Layouts/SiteLayout";
import { Head, usePage } from "@inertiajs/react";
import useT from "@frontend/js/hooks/useT";
import useRoute from "@frontend/js/hooks/useRoute";
import { useState, useMemo } from "react";

export default function Methodology({
    page,
    resource,
    meta = {
        title: "",
        description: "",
    },
}) {
    const { t } = useT("site");
    const route = useRoute();
    const methodology = resource?.data;
    const pageProps = usePage().props;
    const locale = pageProps.currentLocale;
    const content = methodology?.content || "";
    const pageTitle = page?.title?.[locale] || "";
    const pageHref = route("frontend.page", { page: page?.slug });
    const [isRequestFormOpen, setIsRequestFormOpen] = useState(false);
    const animationId = methodology?.animation_id;

    const memoizedContent = useMemo(() => ({ __html: content }), [content]);

    async function handleSubmit(
        formData,
        { reset, clearErrors, setError, source, sourceId },
    ) {
        try {
            const csrfToken = document
                .querySelector('meta[name="csrf-token"]')
                ?.getAttribute("content");

            const response = await fetch("/contact-form", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    "X-CSRF-TOKEN": csrfToken || "",
                },
                body: JSON.stringify({
                    ...formData,
                    locale,
                    source: source || 3,
                    ...(sourceId && { sourceId }),
                }),
            });

            const text = await response.text();

            if (response.status === 422) {
                const result = JSON.parse(text);
                if (result.errors) {
                    Object.entries(result.errors).forEach(
                        ([field, messages]) => {
                            setError(field, messages[0]);
                        },
                    );
                }
                return { validationErrors: true };
            }

            if (!response.ok) {
                console.error("Form submission error:", text);
                return false;
            }

            const result = JSON.parse(text);

            if (!result.success) {
                return false;
            }

            reset();
            return true;
        } catch (error) {
            console.error("Form submission error:", error);
            return false;
        }
    }

    const {
        sections,
        activeSection,
        contentContainerRef,
        handleNavigationClick,
    } = useSectionNavigation(content);

    return (
        <>
            <MetaHead meta={meta} />

            <SiteLayout>
                <div className="methodology page-shell">
                    <PageTitleBreadcrumb
                        title={methodology?.title}
                        parentTitle={pageTitle}
                        parentHref={pageHref}
                    />
                    <div className="methodology__container">
                        <div
                            className="methodology__container__text"
                            ref={contentContainerRef}
                            dangerouslySetInnerHTML={memoizedContent}
                        />
                        <div className="methodology__container__navigation">
                            <AnimationIcon
                                animationId={animationId}
                                className="methodology__container__navigation__icon"
                            />
                            <div className="methodology__container__navigation__list">
                                {sections.map((section) => (
                                    <button
                                        key={section.id}
                                        type="button"
                                        className={`methodology__container__navigation__list__link ${activeSection === section.id ? "is-active" : ""}`}
                                        aria-current={
                                            activeSection === section.id
                                                ? "true"
                                                : undefined
                                        }
                                        onClick={() =>
                                            handleNavigationClick(section.id)
                                        }
                                    >
                                        {section.title}
                                    </button>
                                ))}
                            </div>
                            <button
                                className="methodology__container__navigation__cta"
                                type="button"
                                onClick={() => setIsRequestFormOpen(true)}
                            >
                                {t("methodology-request-offer-button")}{" "}
                                <ArrowNextIcon />
                            </button>
                        </div>
                    </div>
                    <Form
                        variant="drawer"
                        isOpen={isRequestFormOpen}
                        onClose={() => setIsRequestFormOpen(false)}
                        source={3}
                        sourceId={methodology?.id}
                        onSubmit={handleSubmit}
                    />
                </div>
            </SiteLayout>
        </>
    );
}
